import { AppliedFilters } from '../models/AppliedFilters';
import { CategoryFilter } from '../models/CategoryFilter';
import { FieldFilter } from '../models/FieldFilter';
import { GroupedFieldFilters } from '../models/GroupedFieldFilter';

export function transformToGroupedFieldFilters(
    availableFieldFilters: FieldFilter[],
    appliedFieldFilters: FieldFilter[]
): GroupedFieldFilters[] {
    return availableFieldFilters.reduce((previous, current) => {
        const existingGroup = previous.find(
            group => group.fieldName === current.fieldName
        );
        const isSelected = appliedFieldFilters.some(
            f =>
                f.fieldName === current.fieldName &&
                f.fieldValue === current.fieldValue
        );
        if (existingGroup) {
            existingGroup.fieldValues.push({
                fieldValue: current.fieldValue,
                fieldTranslatedValue:
                    current.fieldTranslatedValue || current.fieldValue,
                isSelected,
                color: current.color
            });
            existingGroup.quantitySelected += isSelected ? 1 : 0;
        } else {
            previous.push({
                fieldName: current.fieldName,
                fieldTranslatedName:
                    current.fieldTranslatedName || current.fieldName,
                fieldType: current.fieldType,
                quantitySelected: isSelected ? 1 : 0,
                isFeaturedFilterInUI: current.isFeaturedFilterInUI || false,
                fieldValues: [
                    {
                        fieldValue: current.fieldValue,
                        fieldTranslatedValue:
                            current.fieldTranslatedValue || current.fieldValue,
                        isSelected,
                        color: current.color
                    }
                ]
            });
        }
        return previous;
    }, [] as GroupedFieldFilters[]);
}

export function toggleFieldFilter(
    availableFieldFilters: FieldFilter[],
    appliedFieldFilters: FieldFilter[],
    fieldName: string,
    fieldValue: string
): FieldFilter[] {
    const existingInApplied = appliedFieldFilters.find(
        filter =>
            filter.fieldName === fieldName && filter.fieldValue === fieldValue
    );
    const existingInAvailable = availableFieldFilters.find(
        filter =>
            filter.fieldName === fieldName && filter.fieldValue === fieldValue
    ) as FieldFilter;
    return existingInApplied
        ? appliedFieldFilters.filter(
              filter =>
                  !(
                      filter.fieldName === fieldName &&
                      filter.fieldValue === fieldValue
                  )
          )
        : [
              ...appliedFieldFilters,
              {
                  ...existingInAvailable
              }
          ];
}

export function toggleCategoryFilter(
    availableCategoryFilters: CategoryFilter[],
    appliedCategoryFilters: CategoryFilter[],
    categoryValue: string
): CategoryFilter[] {
    const existingInApplied = appliedCategoryFilters.find(
        filter => filter.categoryValue === categoryValue
    );
    const existingInAvailable = availableCategoryFilters.find(
        filter => filter.categoryValue === categoryValue
    ) as CategoryFilter;
    return existingInApplied
        ? appliedCategoryFilters.filter(
              filter => !(filter.categoryValue === categoryValue)
          )
        : [
              ...appliedCategoryFilters,
              {
                  ...existingInAvailable
              }
          ];
}

function areAppliedCategoryFiltersEqual(
    filters1?: CategoryFilter[],
    filters2?: CategoryFilter[]
): boolean {
    if ((filters1 && !filters2) || (!filters1 && filters2)) {
        return false;
    }
    if (!filters1 || !filters2) {
        return true;
    }
    if (filters1.length !== filters2.length) {
        return false;
    }

    return filters1.every(filter1 =>
        filters2.some(
            filter2 => filter1.categoryValue === filter2.categoryValue
        )
    );
}

function areAppliedFieldFiltersEqual(
    filters1?: FieldFilter[],
    filters2?: FieldFilter[]
): boolean {
    if ((filters1 && !filters2) || (!filters1 && filters2)) {
        return false;
    }
    if (!filters1 || !filters2) {
        return true;
    }
    if (filters1.length !== filters2.length) return false;

    return filters1.every(filter1 =>
        filters2.some(
            filter2 =>
                filter1.fieldValue === filter2.fieldValue &&
                filter1.fieldName === filter2.fieldName
        )
    );
}

export function areAppliedFiltersEquals(
    appliedFilters1: Partial<AppliedFilters>,
    appliedFilters2: Partial<AppliedFilters>
): boolean {
    return (
        appliedFilters1.appliedMaxPrice === appliedFilters2.appliedMaxPrice &&
        appliedFilters1.appliedMinPrice === appliedFilters2.appliedMinPrice &&
        appliedFilters1.appliedSortBy === appliedFilters2.appliedSortBy &&
        areAppliedCategoryFiltersEqual(
            appliedFilters1.appliedCategoryFilters,
            appliedFilters2.appliedCategoryFilters
        ) &&
        areAppliedFieldFiltersEqual(
            appliedFilters1.appliedFieldFilters,
            appliedFilters2.appliedFieldFilters
        )
    );
}

export function transformFieldFiltersToTaxonomyTags(
    appliedFieldFilters: FieldFilter[] = []
): string[] {
    return appliedFieldFilters.map(
        filter => `${filter.fieldName}:${filter.fieldValue}`
    );
}

export function transformCategoryFiltersToTaxonomyTags(
    appliedCategoryFilters: CategoryFilter[] = []
): string[] {
    return appliedCategoryFilters.map(
        filter => `_category:${filter.categoryValue}`
    );
}
